/**
 * The Morcast logo, linked to the home page. It shows the mark and the wordmark side by side.
 *
 * The logo is drawn in the page as vector shapes instead of being loaded as an image. That way it
 * shows with the first paint, stays sharp at every size and follows the theme. The shapes are
 * those of the logo's files in docs/brand, which also describes how the logo is built.
 */

import { Link } from "react-router";

import { focusRing } from "./ui.js";

/**
 * The logo's colours. Each one is the logo's own colour on a light page, with a lighter partner
 * for the dark theme's charcoal, where the original navy would disappear.
 */
const FILL = {
  /** The left pillar and the letters "Mor", like the token's name. */
  navy: "fill-[#1f252c] dark:fill-[#f1f4f8]",
  /** The right pillar. */
  blue: "fill-[#3f5b78] dark:fill-[#7c9dbe]",
  /** The band that runs from the left pillar down to the tip of the M. */
  band: "fill-[#4f7094] dark:fill-[#a9c3de]",
  /** The part of the right diagonal that the band leaves in view. */
  shade: "fill-[#304658] dark:fill-[#587a9c]",
  /** The three arcs of the signal. */
  cyan: "fill-[#41a8c6] dark:fill-[#4dbbe0]",
  /** The letters "cast". */
  slate: "fill-[#2c3f4f] dark:fill-[#a7b9cb]",
};

/**
 * Each arc dims once and comes back when the pointer comes over the logo (`signal` in styles.css).
 * The smallest arc goes first and the largest last, so the dimming travels outwards like a signal.
 * Readers who asked their system for less motion see no change.
 */
const SIGNAL = {
  inner: "motion-safe:group-hover:animate-signal-inner",
  middle: "motion-safe:group-hover:animate-signal-middle",
  outer: "motion-safe:group-hover:animate-signal-outer",
};

export function Logo() {
  return (
    // The picture is hidden from screen readers, which read the link's label instead.
    <Link to="/" aria-label="Morcast" className={`group inline-flex rounded-lg ${focusRing}`}>
      <svg viewBox="0 0 318 100" aria-hidden="true" className="h-8 w-auto">
        {/* The mark. Each shape covers the edge of the ones before it, so no seam shows. */}
        <path className={FILL.shade} d={MARK.shade} />
        <path className={FILL.band} d={MARK.band} />
        <path className={FILL.blue} d={MARK.rightPillar} />
        <path className={FILL.navy} d={MARK.leftPillar} />
        <g className={FILL.cyan}>
          <path className={SIGNAL.outer} d={MARK.outerArc} />
          <path className={SIGNAL.middle} d={MARK.middleArc} />
          <path className={SIGNAL.inner} d={MARK.innerArc} />
        </g>
        {/* The wordmark. "Mor" takes the left pillar's navy and "cast" a slate. */}
        <g className={FILL.navy}>
          <path d={LETTERS.M} />
          <path d={LETTERS.o} />
          <path d={LETTERS.r} />
        </g>
        <g className={FILL.slate}>
          <path d={LETTERS.c} />
          <path d={LETTERS.a} />
          <path d={LETTERS.s} />
          <path d={LETTERS.t} />
        </g>
      </svg>
    </Link>
  );
}

/*
 * The shapes below are in the units of the picture's box, 318 wide and 100 high. The mark fills
 * the box's height. The wordmark's capitals are 42% of that height, and its baseline sits 16% above
 * the bottom, which centres the word on the M's body.
 */

/**
 * The mark's shapes. The M is symmetric about its centre line. Its corners are arcs of circles,
 * and the three arcs above it are strokes of one width about one centre.
 */
const MARK = {
  shade:
    "M41.52 51.93A4.6 4.6 0 0 0 46.34 51.43L62.65 38.77L63.01 39.48L63.01 64.42L62.65 65.13L60.76 66.55L59.97 66.4L41.87 52.65Z",
  band: "M24.39 38.77L40.7 51.43A4.6 4.6 0 0 0 41.52 51.93L60.76 66.55L46.76 77.01A5.41 5.41 0 0 1 40.28 77.01L24.39 65.13L24.03 64.42L24.03 39.48Z",
  rightPillar:
    "M62.65 38.77L79.29 25.85A4.66 4.66 0 0 1 86.82 29.54L86.82 95.09A4.91 4.91 0 0 1 79.06 99.09L63.84 88.28A2.81 2.81 0 0 1 62.65 85.98L62.65 38.77Z",
  leftPillar:
    "M0.22 95.09L0.22 29.54A4.66 4.66 0 0 1 7.75 25.85L24.39 38.77L24.39 85.98A2.81 2.81 0 0 1 23.2 88.28L7.98 99.09A4.91 4.91 0 0 1 0.22 95.09Z",
  outerArc:
    "M12.73 14.18A40.52 40.52 0 0 1 74.31 14.18A3.29 3.29 0 0 1 69.31 18.46A33.93 33.93 0 0 0 17.73 18.46A3.29 3.29 0 0 1 12.73 14.18Z",
  middleArc:
    "M23.32 23.57A26.36 26.36 0 0 1 63.72 23.57A3.29 3.29 0 0 1 58.67 27.8A19.78 19.78 0 0 0 28.37 27.8A3.29 3.29 0 0 1 23.32 23.57Z",
  innerArc:
    "M33.54 33.95A11.94 11.94 0 0 1 53.5 33.95A3.29 3.29 0 0 1 48 37.57A5.36 5.36 0 0 0 39.04 37.57A3.29 3.29 0 0 1 33.54 33.95Z",
};

/**
 * The wordmark's letters. Straight edges are exact lines, the o is two ellipses about one centre,
 * and the other curves are cubic curves that join without a kink.
 */
const LETTERS = {
  M: "M112.8 83.38L112.8 42.62A0.62 0.62 0 0 1 113.42 42L120.23 42A0.98 0.98 0 0 1 121.01 42.39L134.89 60.61L148.77 42.39A0.98 0.98 0 0 1 149.55 42L156.36 42A0.62 0.62 0 0 1 156.98 42.62L156.98 83.38A0.62 0.62 0 0 1 156.36 84L149.17 84A0.62 0.62 0 0 1 148.54 83.38L148.54 56.43L135.62 72.82A0.93 0.93 0 0 1 134.16 72.82L121.23 56.43L121.23 83.38A0.62 0.62 0 0 1 120.61 84L113.42 84A0.62 0.62 0 0 1 112.8 83.38Z",
  o: "M160.38 68.93A16.26 15.64 0 1 1 192.91 68.93A16.26 15.64 0 1 1 160.38 68.93ZM168.11 68.93A8.54 8.7 0 1 0 185.19 68.93A8.54 8.7 0 1 0 168.11 68.93Z",
  r: "M196.27 83.38L196.27 53.91A0.62 0.62 0 0 1 196.9 53.29L203.44 53.29A0.62 0.62 0 0 1 204.06 53.91L204.06 56.82C206.64 54.05 209.4 53.02 213.16 53.16A0.62 0.62 0 0 1 213.76 53.78L213.76 59.35A0.62 0.62 0 0 1 213.12 59.97C207.63 59.76 204.06 63.55 204.06 68.96L204.06 83.38A0.62 0.62 0 0 1 203.44 84L196.9 84A0.62 0.62 0 0 1 196.27 83.38Z",
  c: "M242.65 60.61L237.38 63.97A0.41 0.41 0 0 1 236.83 63.86C235.18 61.57 232.43 60.21 229.58 60.21C225.07 60.21 221.14 64.35 221.14 69.12C221.14 73.92 224.73 77.74 229.58 77.74C232.7 77.74 235.16 76.03 236.93 73.59A0.41 0.41 0 0 1 237.46 73.48L242.81 76.58A0.41 0.41 0 0 1 242.95 77.14C240.22 81.76 234.9 84.57 229.58 84.57C220.55 84.57 213.47 78.31 213.47 69.12C213.47 60.43 221.05 53.29 229.58 53.29C235.39 53.29 239.24 55.52 242.75 60.02A0.41 0.41 0 0 1 242.65 60.61Z",
  a: "M265.82 65.86C265.67 61.91 263.07 59.97 259.3 59.97C256.08 59.97 253.21 61.28 250.71 63.22A0.41 0.41 0 0 1 250.15 63.16L246.61 58.95A0.41 0.41 0 0 1 246.63 58.4C249.91 55.06 254.67 53.29 259.3 53.29C266.94 53.29 273.65 57.43 273.65 65.75L273.65 83.38A0.62 0.62 0 0 1 273.02 84L266.62 84A0.62 0.62 0 0 1 266 83.38L266 80.75C263.22 83.14 260.06 84.57 256.35 84.57C250.78 84.57 244.59 81.36 244.59 75.05C244.59 73.26 244.9 71.66 245.93 70.17C249.97 64.31 259.71 65.5 265.82 65.86ZM265.69 71.66C263.57 71.33 260.77 71.1 258.61 71.1C256.22 71.1 252.38 71.43 252.38 74.61C252.38 77.45 255.5 78.14 257.77 78.14C261.89 78.14 265.49 76.15 265.69 71.66Z",
  s: "M275.97 78.9L280.37 74.93A0.41 0.41 0 0 1 280.94 74.95C282.66 76.74 285.09 78.14 287.64 78.14C289.39 78.14 291.8 77.47 291.8 75.31C291.8 73.36 289.95 72.9 288.53 72.53C283.28 71.18 276.84 69.79 276.84 63.01C276.84 56.58 282.38 53.29 288.34 53.29C292.19 53.29 296.4 54.69 298.85 57.86A0.41 0.41 0 0 1 298.82 58.4L294.82 62.41A0.41 0.41 0 0 1 294.25 62.42C292.65 60.86 290.63 59.69 288.34 59.69C286.69 59.69 284.5 60.49 284.5 62.47C284.5 67.77 299.45 64.12 299.45 74.9C299.45 81.2 293.34 84.57 287.64 84.57C283.06 84.57 279.05 82.84 275.94 79.47A0.41 0.41 0 0 1 275.97 78.9Z",
  t: "M302.43 47.2L309.01 47.2A0.62 0.62 0 0 1 309.64 47.82L309.64 53.29L317.15 53.29A0.62 0.62 0 0 1 317.78 53.91L317.78 59.27A0.62 0.62 0 0 1 317.15 59.9L309.64 59.9L309.64 73.09C309.64 75.96 311.5 77.31 314.21 77.31C315.11 77.31 316.14 77.2 317.02 77A0.62 0.62 0 0 1 317.78 77.61L317.78 83.06A0.62 0.62 0 0 1 317.33 83.66C315.82 84.1 314.24 84.38 312.66 84.38C305.99 84.38 301.81 80.55 301.81 73.86L301.81 47.82A0.62 0.62 0 0 1 302.43 47.2Z",
};
